import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import * as tiimStudyParticipantActions from './tiim-study-participant.actions';
import { DashboardCamelCasePipe } from '../../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from '../../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { AppState } from '../../../../state';
import { CustomRouterNavigatedAction } from '../../../../core/custom-route-serializer';
import { STUDIES_ROUTES } from '../../../studies/studies.routes';
import { StudyParticipantsService } from '../../../services/study-participants/study-participants.service';

@Injectable()
export class TiimStudyParticipantEffects {
  tiimStudyParticipantsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) =>
        route.payload.routerState.path.endsWith(STUDIES_ROUTES.TIIM_PARTICIPANTS)
      ),
      map((action) =>
        tiimStudyParticipantActions.getTiimStudyParticipants({
          studyId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getTiimStudyParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tiimStudyParticipantActions.getTiimStudyParticipants),
      switchMap(({ studyId }) => {
        return this.studyParticipantsService.getTiimStudyParticipants(studyId).pipe(
          map((tiimStudyParticipants) => {
            const transformedTiimStudyParticipants = [];
            for (const tiimStudyParticipant of tiimStudyParticipants) {
              const transformedTiimStudyParticipant = this.dashboardCamelCasePipe.keysToCamelCase(tiimStudyParticipant);
              transformedTiimStudyParticipants.push(transformedTiimStudyParticipant);
            }
            return tiimStudyParticipantActions.getTiimStudyParticipantsSuccess({
              tiimStudyParticipants: transformedTiimStudyParticipants,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              tiimStudyParticipantActions.getTiimStudyParticipantsError({
                error,
              })
            )
          )
        );
      })
    )
  );

  deleteTiimStudyParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tiimStudyParticipantActions.deleteTiimStudyParticipant),
      switchMap(({ studyId, participantId, tiimStudyParticipantId }) => {
        return this.studyParticipantsService.deleteStudyParticipant(studyId, participantId).pipe(
          map(() =>
            tiimStudyParticipantActions.deleteTiimStudyParticipantSuccess({
              deletedTiimStudyParticipantId: tiimStudyParticipantId,
            })
          ),
          catchError((err: HttpErrorResponse) =>
            of(
              tiimStudyParticipantActions.deleteTiimStudyParticipantError({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  deleteTiimStudyParticipantSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.deleteTiimStudyParticipantSuccess),
        tap(({ deletedTiimStudyParticipantId }) => this.toastrService.success(`Participant deleted successfully`))
      ),
    { dispatch: false }
  );

  deleteTiimStudyParticipantError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.deleteTiimStudyParticipantError),
        tap(() => this.toastrService.error(`Unable to delete participant`))
      ),
    { dispatch: false }
  );

  sendTiimStudyParticipantNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tiimStudyParticipantActions.sendTiimStudyParticipantNotification),
      switchMap(({ participantId, message }) => {
        return this.studyParticipantsService.sendTiimStudyParticipantNotification(participantId, message).pipe(
          map(() => tiimStudyParticipantActions.sendTiimStudyParticipantNotificationSuccess()),
          catchError((err: HttpErrorResponse) =>
            of(tiimStudyParticipantActions.sendTiimStudyParticipantNotificationError({ error: err }))
          )
        );
      })
    )
  );

  sendTiimStudyParticipantNotificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.sendTiimStudyParticipantNotificationSuccess),
        tap(() => this.toastrService.success(`Message sent!`))
      ),
    { dispatch: false }
  );

  sendTiimStudyParticipantNotificationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.sendTiimStudyParticipantNotificationError),
        tap(() => this.toastrService.error(`Unable to send message`))
      ),
    { dispatch: false }
  );

  enrolTiimStudyParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tiimStudyParticipantActions.enrolTiimStudyParticipant),
      switchMap(({ tiimStudyParticipant }) => {
        return this.studyParticipantsService
          .enrolTiimStudyParticipant(this.dashboardSnakeCase.keysToSnakeCase(tiimStudyParticipant))
          .pipe(
            map((enrolledTiimStudyParticipant) =>
              tiimStudyParticipantActions.enrolTiimStudyParticipantSuccess({
                enrolledTiimStudyParticipant: this.dashboardCamelCasePipe.keysToCamelCase(enrolledTiimStudyParticipant),
              })
            ),
            catchError((err: HttpErrorResponse) =>
              of(
                tiimStudyParticipantActions.enrolTiimStudyParticipantError({
                  error: err,
                })
              )
            )
          );
      })
    )
  );

  enrolTiimStudyParticipantSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.enrolTiimStudyParticipantSuccess),
        tap(({ enrolledTiimStudyParticipant }) => this.toastrService.success(`Participant saved!`))
      ),
    { dispatch: false }
  );

  enrolTiimStudyParticipantError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(tiimStudyParticipantActions.enrolTiimStudyParticipantError),
        tap(() => this.toastrService.error(`Unable to save participant`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private studyParticipantsService: StudyParticipantsService,
    private dashboardSnakeCase: DashboardSnakeCasePipe,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private toastrService: ToastrService
  ) {}
}
